/* ==========================================================================
   WAYPOINT — where a trip is, in words.

   Every value here arrives already resolved from the server (stop.locality /
   stop.region / stop.siteType, and the trip-summary rollups that mirror
   them) — this file only decides how much of it fits on a line. The rules
   are shared between the trip card, the trip detail header and the stop
   card so the same trip never describes itself two different ways.
   ========================================================================== */

import type { SiteType, Stop, TripSummary } from '../../../lib/types';

/** Singular, for one stop's chip. Mirrors SITE_TYPE_LABELS in
 *  server/src/domain/siteType.ts — the server sends the enum, not the prose, because the label is
 *  a rendering decision and the enum is the fact. */
export const SITE_TYPE_LABELS: Record<SiteType, string> = {
  state_park: 'State park',
  national_park: 'National park',
  county_park: 'County park',
  rv_park: 'RV park',
  private: 'Private stay',
};

/** Plural, for the "3 state parks · 2 RV parks" rollup on a trip card. */
export const SITE_TYPE_LABELS_PLURAL: Record<SiteType, string> = {
  state_park: 'state parks',
  national_park: 'national parks',
  county_park: 'county parks',
  rv_park: 'RV parks',
  private: 'private stays',
};

/** Mid-sentence singular, spelled out rather than derived by lowercasing SITE_TYPE_LABELS —
 *  which produced "1 rv park". "RV" is an initialism and stays capitalized wherever it lands. */
const SITE_TYPE_LABELS_SINGULAR: Record<SiteType, string> = {
  state_park: 'state park',
  national_park: 'national park',
  county_park: 'county park',
  rv_park: 'RV park',
  private: 'private stay',
};

/** "3 state parks", "1 RV park" — the count and the right label for it. */
export function siteTypeCountLabel(entry: { type: SiteType; count: number }): string {
  const noun = entry.count === 1 ? SITE_TYPE_LABELS_SINGULAR[entry.type] : SITE_TYPE_LABELS_PLURAL[entry.type];
  return `${entry.count} ${noun}`;
}

/** "Somerset, PA" for one stop, or '' when neither half has resolved yet. Never renders a stray
 *  comma: a stop OSM could only place to the state reads "PA", not ", PA". */
export function stopPlaceLabel(stop: Pick<Stop, 'locality' | 'region'>): string {
  const locality = (stop.locality ?? '').trim();
  const region = (stop.region ?? '').trim();
  if (locality && region) return `${locality}, ${region}`;
  return locality || region;
}

/**
 * One line naming where a trip goes, from its ordered distinct "Town, ST" labels.
 *
 * Up to two towns are listed in full, because that is the whole route for the many small
 * stay-pair trips Jeff actually plans. Three or more collapse to first → last: a card is one line
 * wide and "Somerset, PA · Reston, VA · Lisbon, OH · Auburn, IN" ellipsizes into nonsense, while
 * the endpoints are the two facts that answer "where was this one?".
 */
export function tripPlaceLine(localities: string[] | undefined): string {
  const list = (localities ?? []).filter(Boolean);
  if (list.length === 0) return '';
  if (list.length <= 2) return list.join(' · ');
  return `${list[0]} → ${list[list.length - 1]}`;
}

/**
 * The states a trip actually covers, spelled out for the trip detail header — "Pennsylvania ·
 * Ohio · Indiana". Long names because the header has the room and they read like a place rather
 * than a postal form; past four the tail collapses to a count so a coast-to-coast run can't push
 * the title block around.
 */
export function tripRegionLine(regionNames: string[] | undefined, max = 4): string {
  const list = (regionNames ?? []).filter(Boolean);
  if (list.length === 0) return '';
  if (list.length <= max) return list.join(' · ');
  return `${list.slice(0, max).join(' · ')} +${list.length - max} more`;
}

/** The rollups a TripSummary carries, derived instead from a full trip's stops.
 *
 *  Used where the client has the stops but not a freshly-loaded summary — the trip detail header,
 *  and lib/store.ts's local synthesis after a write. Same ordering rules as the server's
 *  placeRollup(): route order, first appearance wins, duplicates collapsed. */
export function rollupStopPlaces(stops: Stop[]): Pick<TripSummary, 'localities' | 'regions' | 'regionNames' | 'siteTypes'> {
  const localities: string[] = [];
  const regions: string[] = [];
  const regionNames: string[] = [];
  const tally = new Map<SiteType, number>();
  for (const stop of [...stops].sort((a, b) => a.seq - b.seq)) {
    const label = stopPlaceLabel(stop);
    if (label && !localities.includes(label)) localities.push(label);
    const region = (stop.region ?? '').trim();
    if (region && !regions.includes(region)) {
      regions.push(region);
      regionNames.push((stop.regionName ?? '').trim() || region);
    }
    if (stop.siteType) tally.set(stop.siteType, (tally.get(stop.siteType) ?? 0) + 1);
  }
  const order: SiteType[] = ['state_park', 'national_park', 'county_park', 'rv_park', 'private'];
  const siteTypes = order
    .filter((t) => tally.has(t))
    .map((type) => ({ type, count: tally.get(type)! }))
    .sort((a, b) => b.count - a.count || order.indexOf(a.type) - order.indexOf(b.type));
  return { localities, regions, regionNames, siteTypes };
}
